import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './DocDateField.module.css';
import { cx } from '../../lib/cx';

/**
 * Sdílený kalendářový popover pro datumové vstupy (DocDateField i DocInlineDate).
 *
 * Renderuje se přes React portal do <body> s `position: fixed` a souřadnicemi
 * spočítanými z tlačítka (anchoru). Tím se vyhne ořezu jakýmkoli scrollovacím
 * kontejnerem (`overflow: auto` na .body dokumentu apod.), který dříve kalendář
 * „rozbíjel" — viz hlášení „špatně se zobrazuje kalendář, všude".
 */

const CZ_MONTHS = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];
const CZ_DAYS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

export function formatCz(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${d.getFullYear()}`;
}

export function parseCz(v: string): Date | null {
  if (!v) return null;
  const cleaned = v.trim();
  const m1 = cleaned.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (m1) return new Date(parseInt(m1[3]), parseInt(m1[2]) - 1, parseInt(m1[1]));
  const m2 = cleaned.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return new Date(parseInt(m2[1]), parseInt(m2[2]) - 1, parseInt(m2[3]));
  const dt = new Date(cleaned);
  return isNaN(dt.getTime()) ? null : dt;
}

const POP_W = 320;

export interface CalendarPopoverProps {
  /** Tlačítko/kotva, vůči které se popover umístí. */
  anchorEl: HTMLElement | null;
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

export function CalendarPopover({ anchorEl, value, onChange, onClose }: CalendarPopoverProps) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const initial = parseCz(value) ?? new Date();
  const [viewY, setViewY] = useState(initial.getFullYear());
  const [viewM, setViewM] = useState(initial.getMonth());

  // Při otevření naskoč na měsíc vybraného data.
  useEffect(() => {
    const d = parseCz(value);
    if (d) {
      setViewY(d.getFullYear());
      setViewM(d.getMonth());
    }
    // jen při mountu / změně hodnoty zvenčí
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Umísti popover vůči anchoru; přepočítej při scrollu/resize.
  useLayoutEffect(() => {
    function place() {
      if (!anchorEl) return;
      const r = anchorEl.getBoundingClientRect();
      const popH = popRef.current?.offsetHeight ?? 380;
      let left = r.right - POP_W; // zarovnáno na pravý okraj tlačítka
      left = Math.max(8, Math.min(left, window.innerWidth - 8 - POP_W));
      let top = r.bottom + 6;
      if (top + popH > window.innerHeight - 8) {
        const above = r.top - 6 - popH;
        top = above >= 8 ? above : Math.max(8, window.innerHeight - 8 - popH);
      }
      setPos({ top, left });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorEl, viewY, viewM]);

  // Zavření kliknutím mimo / Esc.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchorEl?.contains(t)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorEl, onClose]);

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  const selected = parseCz(value);
  const selectedKey = selected ? `${selected.getFullYear()}-${selected.getMonth()}-${selected.getDate()}` : null;

  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const firstDow = (new Date(viewY, viewM, 1).getDay() + 6) % 7; // Pondělí = 0

  const cells: Array<{ d: number; key: string } | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push({ d, key: `${viewY}-${viewM}-${d}` });

  function pick(day: number) {
    onChange(formatCz(new Date(viewY, viewM, day)));
    onClose();
  }

  function navMonth(dir: 1 | -1) {
    let m = viewM + dir;
    let y = viewY;
    if (m > 11) { m = 0; y++; }
    if (m < 0) { m = 11; y--; }
    setViewM(m);
    setViewY(y);
  }

  return createPortal(
    <div
      ref={popRef}
      className={cx(styles.popover, styles.popoverFixed)}
      role="dialog"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
    >
      <div className={styles.calHead}>
        <button type="button" className={styles.navBtn} onClick={() => setViewY(viewY - 1)}>‹‹</button>
        <button type="button" className={styles.navBtn} onClick={() => navMonth(-1)}>‹</button>
        <div className={styles.calTitle}>
          {CZ_MONTHS[viewM]} <span className={styles.year}>{viewY}</span>
        </div>
        <button type="button" className={styles.navBtn} onClick={() => navMonth(1)}>›</button>
        <button type="button" className={styles.navBtn} onClick={() => setViewY(viewY + 1)}>››</button>
      </div>
      <div className={styles.calGrid}>
        {CZ_DAYS.map((d) => (
          <div key={d} className={styles.dayHead}>{d}</div>
        ))}
        {cells.map((c, i) => {
          if (!c) return <div key={`e${i}`} />;
          const isToday = c.key === todayKey;
          const isSelected = c.key === selectedKey;
          return (
            <button
              key={c.key}
              type="button"
              className={cx(styles.dayCell, isToday && styles.today, isSelected && styles.selected)}
              onClick={() => pick(c.d)}
            >
              {c.d}
            </button>
          );
        })}
      </div>
      <div className={styles.calFoot}>
        <button
          type="button"
          className={styles.footBtn}
          onClick={() => {
            onChange(formatCz(new Date()));
            onClose();
          }}
        >
          Dnes
        </button>
        <button
          type="button"
          className={cx(styles.footBtn, styles.ghost)}
          onClick={() => {
            onChange('');
            onClose();
          }}
        >
          Vymazat
        </button>
      </div>
    </div>,
    document.body,
  );
}
